import React, { useEffect, useState } from 'react';
import {
  useGetInvestorPreferences,
  useUpdateInvestorPreferences,
} from '@/lib/api/investor-preferences.ts';
import { investmentTypeLabels } from '@/features/investments/constants.ts';
import { InvestmentType } from '@inversor-hub/types';
import {
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  FormGroup,
  RadioGroup,
  Radio,
  Box,
  Button,
} from '@mui/material';
import ProvincesSelector from '@/components/form/ProvincesSelector.tsx';
import EurCurrencyField from '@/components/form/EurCurrencyField.tsx';
import { enqueueSnackbar } from 'notistack';
import { StackedSnackbar } from '@/components/ui/StackedSnackbar.tsx';
import PercentageField from '@/components/form/PercentageField.tsx';

const InvestorConfigSection: React.FC = () => {
  const { data, isLoading } = useGetInvestorPreferences();
  const { mutate: updateInvestorPreferences, isPending } = useUpdateInvestorPreferences();
  const [averageTicket, setAverageTicket] = useState<number | null>(null);
  const [expectedGrossReturn, setExpectedGrossReturn] = useState<number | null>(null);
  const [preferredProvinces, setPreferredProvinces] = useState<string[]>([]);
  const [requiresMortgage, setRequiresMortgage] = useState(true);
  const [investmentTypes, setInvestmentTypes] = useState<InvestmentType[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (data) {
      setAverageTicket(data.averageTicket ?? null);
      setPreferredProvinces(data.preferredProvinces ?? []);
      setRequiresMortgage(data.requiresMortgage ?? true);
      setInvestmentTypes(data.desiredInvestmentTypes ?? []);
      setExpectedGrossReturn(data.expectedGrossReturn ?? null);
    }
  }, [data]);

  const handleInvestmentTypeChange = (type: InvestmentType) => {
    setInvestmentTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const isInvestorValid = !!averageTicket && investmentTypes.length > 0;

  const handleSave = () => {
    setError('');
    updateInvestorPreferences(
      {
        requiresMortgage,
        desiredInvestmentTypes: investmentTypes,
        preferredProvinces,
        averageTicket: averageTicket || 0,
        expectedGrossReturn: expectedGrossReturn || 0,
      },
      {
        onSuccess: () =>
          enqueueSnackbar(<StackedSnackbar title="Configuración de inversor actualizada" />, {
            variant: 'success',
          }),
        onError: () => setError('Error al guardar los cambios'),
      }
    );
  };

  if (isLoading) return <Typography>Cargando configuración de inversor...</Typography>;

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} mb={2}>
        Parámetros de configuración de inversor
      </Typography>
      <Stack spacing={2}>
        <EurCurrencyField
          label="¿Cuánto capital te gustaría invertir?"
          value={averageTicket}
          onChange={setAverageTicket}
          allowDecimalScale={false}
        />
        <PercentageField
          id={'historicalAvgReturn'}
          label="Rentabilidad bruta esperada (%)"
          value={expectedGrossReturn}
          onChange={setExpectedGrossReturn}
          allowDecimalScale={true}
        />
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Tipos de operación preferidas
          </Typography>
          <FormGroup>
            {Object.entries(investmentTypeLabels).map(([value, label]) => (
              <FormControlLabel
                key={value}
                control={
                  <Checkbox
                    checked={investmentTypes.includes(value as InvestmentType)}
                    onChange={() => handleInvestmentTypeChange(value as InvestmentType)}
                  />
                }
                label={label}
              />
            ))}
          </FormGroup>
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            ¿Necesitas hipoteca?
          </Typography>
          <RadioGroup
            row
            value={requiresMortgage.toString()}
            onChange={(e) => setRequiresMortgage(e.target.value === 'true')}
          >
            <FormControlLabel value="true" control={<Radio />} label="Sí" />
            <FormControlLabel value="false" control={<Radio />} label="No" />
          </RadioGroup>
        </Box>
        <ProvincesSelector
          value={preferredProvinces}
          onChange={setPreferredProvinces}
          label="Provincias de interés"
        />
        <Box display="flex" gap={2} alignItems="center">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!isInvestorValid || isPending}
          >
            Guardar cambios
          </Button>
          {error && <Typography color="error.main">{error}</Typography>}
        </Box>
      </Stack>
    </Box>
  );
};

export default InvestorConfigSection;
